import "./__queries__/featuredContent";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import WebMercatorViewport, { Bounds } from "viewport-mercator-project";
import { FEATURE_FLAG_NAME } from "common/constants";
import { HELPSCOUT_BEACON_PAGE } from "common/constants";
import { buildLink, getPath } from "common/routing";
import {
  useGetFeaturedContentForGuestQuery,
  useGetOrCreateGuestPropertyLazyQuery,
} from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { setTitle } from "../../utils/title";
import { track } from "../../utils/tracking";
import { getCurrentURL } from "../../utils/url";
import { AuthContext } from "../Authorization/AuthContext";
import Icon, { ICON_COLORS, Icons } from "../Common/Icons";
import { Grid } from "../Common/Layout";
import {
  SkipNavigation,
  SkipNavigationContent,
} from "../Common/SkipNavigation";
import { Title as TitleTypography } from "../Common/Typography";
import { FeaturedItem } from "../FeaturedContent/FeaturedItem";
import { LayeredMap } from "../Maps/LayeredMap";
import { Viewport } from "../Maps/utils/viewportHook";
import { Search, SearchResultProps } from "../Search";
import {
  Explore,
  FeaturedContentSection,
  FeaturedContentWrapper,
  FooterWrapper,
  GuestWrapper,
  HeaderWrapper,
  Loading,
  MapboxBackground,
  Navigation,
  SearchBox,
  SearchWrapper,
  SectionContainer,
  SectionContent,
  Title,
} from "./__styles__/Guest";
import { Footer, Header } from "./Layout";
import { PublicBanner } from "./PublicBanner";
import { accountTitle, useHelpscoutBeacon } from "./utils";

const CHAMELEON_RANDOM_ID = "5d410d53-5b3a-4323-98a8-7922e4bbf3a9";

const Guestmap = ({ hasFeaturedContent }: { hasFeaturedContent: boolean }) => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const account = useContext(AuthContext).account!;

  const [
    getOrCreateProperty,
    { loading: propertyLoading, data: propertyData },
  ] = useGetOrCreateGuestPropertyLazyQuery();

  const prevLocation = getCurrentURL(location);

  useEffect(() => {
    if (propertyData) {
      const pathname =
        propertyData.getProperty?.id ?
          buildLink("residentProperty", {
            accountId: account.id,
            propertyId: propertyData.getProperty.id,
          })
        : buildLink("noProperty", {
            accountId: account.id,
          });

      history.push({
        pathname,
        state: {
          prevLocation,
        },
      });
    }
  }, [propertyData, account.id, history, prevLocation]);

  const handleSearchResult = (searchResultData: SearchResultProps) => {
    track("Result clicked");

    void getOrCreateProperty({
      variables: {
        address: searchResultData.address,
        longitude: searchResultData.point.longitude,
        latitude: searchResultData.point.latitude,
        id: searchResultData.propertyId,
        parcelId: searchResultData.geocacheParcelId,
        improveParcelCoordinates: !searchResultData.propertyId,
      },
    });
  };

  const mViewport = new WebMercatorViewport({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [viewport, setViewport] = useState<Viewport>({
    ...mViewport.fitBounds(account.bounds as Bounds),
  });

  const layers = {
    accountBoundary: {},
    parcels: {},
    firms: { firms: [] },
  };

  const trackExploreAction = () => {
    track("Map Expanded", {
      source: "search",
    });
  };

  if (propertyLoading) {
    return (
      <Loading>
        <Icon icon={Icons.LOADING} color={ICON_COLORS.LIGHT_GREY} />
      </Loading>
    );
  }

  const title = account.publicPortal.homepageTitle;

  return (
    <>
      <LayeredMap
        height="100%"
        width="100%"
        baseMapStyle={account.defaultBaseMap.mapboxStyle}
        account={account}
        layers={layers}
        viewport={viewport}
        setViewport={setViewport}
        disableInteraction={true}
        shader={{ hasFeaturedContent }}
      />
      <Navigation hasFeaturedContent={hasFeaturedContent}>
        <SearchWrapper>
          <Title>{title}</Title>
          <SearchBox>
            <Search handleResultClick={handleSearchResult} />
          </SearchBox>
          <Explore
            onClick={trackExploreAction}
            to={{
              pathname: getPath("explore"),
            }}
          >
            {t("button-explore-map")}
          </Explore>
        </SearchWrapper>
        <FooterWrapper>
          <Footer />
        </FooterWrapper>
      </Navigation>
    </>
  );
};

export type GuestLocationState = {
  openLoginModal?: boolean;
};

export const Guest = () => {
  const location = useLocation<GuestLocationState>();
  useHelpscoutBeacon(HELPSCOUT_BEACON_PAGE.HOME);
  const { account, isFeatureEnabled } = useContext(AuthContext);
  const title = account?.publicPortal.navBarTitle;

  useEffect(() => {
    // timeout is here just to make sure chmln is fully loaded
    const timeoutId = setTimeout(() => {
      window.chmln?.identify?.(CHAMELEON_RANDOM_ID, {});
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, []);

  const isMerchandisingEnabled = isFeatureEnabled(
    FEATURE_FLAG_NAME.MERCHANDISING,
  );

  const { data: featuredContentData } = useGetFeaturedContentForGuestQuery({
    skip: !isMerchandisingEnabled,
  });

  const featuredContentSections = featuredContentData?.featuredContent ?? [];

  const hasFeaturedContent =
    featuredContentSections.length > 0 &&
    featuredContentSections.some(
      section => section.featuredItems && section.featuredItems.length > 0,
    );
  const shouldShowFeaturedContent =
    isMerchandisingEnabled && hasFeaturedContent;

  setTitle(`${title} | ${accountTitle()}`);

  return (
    <GuestWrapper>
      <HeaderWrapper>
        <SkipNavigation />
        <PublicBanner />
        <Header openLoginModal={location.state?.openLoginModal ?? false} />
      </HeaderWrapper>
      <SkipNavigationContent>
        <MapboxBackground hasFeaturedContent={hasFeaturedContent}>
          <Guestmap hasFeaturedContent={hasFeaturedContent} />
        </MapboxBackground>
        {shouldShowFeaturedContent && (
          <FeaturedContentWrapper>
            <FeaturedContentSection>
              {featuredContentSections
                .filter(
                  section =>
                    section.featuredItems && section.featuredItems.length > 0,
                )
                .slice()
                .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                .map(section => (
                  <SectionContainer key={section.id}>
                    <SectionContent>
                      <TitleTypography
                        size="small"
                        type="semiBold"
                        style={{
                          alignSelf: "flex-start",
                          paddingBottom: spacing.m.value,
                        }}
                      >
                        {section.title}
                      </TitleTypography>
                      <Grid columns={{ base: 1, M: 2 }} gap={spacing.l}>
                        {section.featuredItems
                          .slice()
                          .sort(
                            (a, b) =>
                              (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
                          )
                          .map(item => (
                            <FeaturedItem
                              key={item.id}
                              image={item.image}
                              title={item.title}
                              description={item.description}
                              link={item.link}
                              entityType={item.entityType}
                              entityId={item.entityId}
                            />
                          ))}
                      </Grid>
                    </SectionContent>
                  </SectionContainer>
                ))}
            </FeaturedContentSection>
            <Footer />
          </FeaturedContentWrapper>
        )}
      </SkipNavigationContent>
    </GuestWrapper>
  );
};

export default Guest;
